/* eslint-disable no-mixed-spaces-and-tabs */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { createContext, FC, useContext, useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { AccountType, UserType, SocialNetworks } from './SignupContext';
import { setProfileDetails } from '../../store/actions/profileActions';
import useUserDetails from '../../hooks/useUserDetails';

export type SocialNetworkName = 'instagram' | 'facebook' | 'tiktok' | 'x';
export type LicenseType = 'personal' | 'standard' | 'commercial';
export type InfluencerCategory = (typeof allInfluencerCategories)[number];

export const allInfluencerCategories = [
  'Artist',
  'Athlete',
  'Chef',
  'Comedian',
  'Fashion',
  'Film and Television',
  'Fitness',
  'Food',
  'Influencer',
  'Literary',
  'Model',
  'Sports',
  'Software',
  'Theater',
  'Voice Talent',
  'Other'
] as const;

export interface SocialNetwork {
  name: SocialNetworkName;
  followers: number;
  handleName: string;
}

export interface Website {
  url: string;
}
export interface PortfolioImage {
  id: string;
  url: string;
  imageUrl?: string;
}

export type InfluencerLicenses = {
  type: string;
  price: number | string;
};

export interface InfluencerResponse {
  influencerProfiles: Influencer[];
}

export interface Influencer {
  id: string;
  userId?: string;
  profileId?: string;
  fullName: string;
  published: boolean;
  categories: string[];
  givenName?: string;
  familyName?: string;
  likesCount?: number; // total number of likes
  averageRating?: number; // average rating value
  userRating?: number | null; // rating given by current user
  profession: string;
  about: string;
  artstyleProfileId?: string | null;
  portfolioImages: Array<PortfolioImage>;
  socialNetworks?: Array<SocialNetwork>;
  website: Website;
  license: InfluencerLicenses;
  licenses: InfluencerLicenses[];
  contentOptions: Array<string>;
  headshot: string;
  coverphoto: string;
  likes: number;
  liked?: boolean;
  collaborated?: boolean;
  userType?: UserType;
  accountType?: AccountType;
  accountName: string;
  companyName?: string;
  collaborations?: string[];
  coverImage?: string;
  pricing?: Record<string, number>;
  projectId?: string;
  facebook?: {
    handle: string;
    followers: number | string;
  };
  instagram?: {
    handle: string;
    followers: number | string;
  };
  tiktok?: {
    handle: string;
    followers: number | string;
  };
  artist?: {
    styleName: string;
    pricing: {
      model: string;
      price: number;
    };
  };
  artStyleId?: null | string;
  credit?: number | null;
}
interface InfluencerProviderProps {
  children?: React.ReactNode;
}
interface InfluencerContextProps {
  influencer: Influencer;
  setInfluencer: React.Dispatch<React.SetStateAction<Influencer>>;
  isLoading: boolean;
}

const InfluencerContext = createContext<InfluencerContextProps | undefined>(undefined);

export const InfluencerProvider: FC<InfluencerProviderProps> = ({ children }) => {
  const { profile, isLoading } = useAppSelector(e => e.profile);
  const { user } = useUserDetails();
  const dispatch = useAppDispatch();

  const socialNetworks: SocialNetworks | undefined = profile?.influencer?.socialNetworks;
  // Use bracket notation to safely access optional properties
  const xData = socialNetworks?.['x'];
  const twitterData = socialNetworks?.['twitter'];
  
  const socialMedia: Array<SocialNetwork> = [
    {
      name: 'instagram',
      followers: socialNetworks?.instagram?.followers ?? 0,
      handleName: socialNetworks?.instagram?.handle ?? ''
    },
    {
      name: 'facebook',
      followers: socialNetworks?.facebook?.followers ?? 0,
      handleName: socialNetworks?.facebook?.handle ?? ''
    },
    {
      name: 'x',
      followers: xData?.followers ?? twitterData?.followers ?? 0,
      handleName: xData?.handle ?? twitterData?.handle ?? ''
    },
    {
      name: 'tiktok',
      followers: socialNetworks?.tiktok?.followers ?? 0,
      handleName: socialNetworks?.tiktok?.handle ?? ''
    }
  ];

  const [influencer, setInfluencer] = useState<Influencer>({
    id: '23',
    userId: '',
    fullName: user?.familyName,
    published: profile?.influencer?.published ?? false,
    categories: profile?.influencer?.categories ?? [],
    profession: profile?.influencer?.profession ?? '',
    about: profile?.influencer?.about ?? '',
    portfolioImages: [],
    socialNetworks: socialMedia,
    website: { url: profile?.influencer?.website ?? '' },
    license: profile?.influencer?.licenses?.[0],
    licenses: profile?.influencer?.licenses,
    contentOptions: profile?.influencer?.contentOptions ?? [],
    headshot: profile?.influencer?.headshot ?? '',
    coverphoto: profile?.influencer?.coverImage ?? '',
    likes: 0,
    liked: false,
    collaborated: false,
    userType: profile?.influencer?.userType,
    accountType: profile?.influencer?.accountType,
    accountName: profile?.influencer?.accountName ?? '',
    companyName: profile?.influencer?.companyName,
    collaborations: profile?.influencer?.collaborations,
    // Always use coverImage from API response
  	coverImage: profile?.influencer?.coverImage ?? '',
    pricing: profile?.influencer?.pricing,
    artstyleProfileId: profile?.influencer?.artstyleProfileId ?? null
  });

  useEffect(() => {
    if (profile?.influencer == null) dispatch(setProfileDetails());
    if (!user || !profile) return;
    setInfluencer({
      id: '23',
      userId: '',
      fullName: user?.familyName,
      published: profile?.influencer?.published ?? false,
      categories: profile?.influencer?.categories ?? [],
      profession: profile?.influencer?.profession ?? '',
      about: profile?.influencer?.about ?? '',
      portfolioImages: [],
      socialNetworks: socialMedia,
      website: { url: profile?.influencer?.website ?? '' },
      license: profile?.influencer?.license,
      licenses: profile?.influencer?.licenses,
      contentOptions: profile?.influencer?.contentOptions ?? [],
      headshot: profile?.influencer?.headshot ?? '',
      coverphoto: profile?.influencer?.coverImage ?? '',
      likes: 0,
      liked: false,
      collaborated: false,
      userType: profile?.influencer?.userType,
      accountType: profile?.influencer?.accountType,
      accountName: profile?.influencer?.accountName ?? '',
      companyName: profile?.influencer?.companyName,
      collaborations: profile?.influencer?.collaborations,
      // Always use coverImage from API response
  	  coverImage: profile?.influencer?.coverImage ?? '',
      pricing: profile?.influencer?.pricing,
      artstyleProfileId: profile?.influencer?.artstyleProfileId
    });
  }, [profile, user]);


  return <InfluencerContext.Provider value={{ influencer, setInfluencer, isLoading }}>{children || <Outlet />}</InfluencerContext.Provider>;
};

export const useInfluencer = (): InfluencerContextProps => {
  const context = useContext(InfluencerContext);
  if (!context) {
    throw new Error('useInfluencer must be used within an InfluencerProvider');
  }
  return context;
};
